import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { Card } from "react-bootstrap";
import {
	FaClock,
	FaBed,
	FaUsers,
	FaCalendarAlt,
	FaPercentage,
	FaHourglassHalf,
	FaHourglassStart,
	FaHourglassEnd,
	FaHourglass,
} from "react-icons/fa";

import { PermanenceAndOccupationMetrics } from "@/services/sintegrada/attendance/getHospitalizationDashboard";

// Função para converter segundos em formato legível (horas e minutos)
function formatTimeFromSeconds(seconds: number): string {
	if (seconds === 0) return "0 min";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);

	if (hours > 0) {
		return `${hours}h ${minutes}min`;
	} else {
		return `${minutes}min`;
	}
}

type Props = {
	permanenceAndOccupationMetrics: PermanenceAndOccupationMetrics;
};

export function PermanenceOccupationMetrics({ permanenceAndOccupationMetrics }: Readonly<Props>) {
	const { permanenceMetrics, occupationRate } = permanenceAndOccupationMetrics;

	return (
		<div className="mb-5">
			{/* Métricas de Permanência */}
			<Row className="mb-4">
				<Col>
					<Card className="border-0 shadow-sm rounded">
						<Card.Header className="bg-white border-bottom py-3">
							<div className="d-flex align-items-center">
								<FaClock className="text-muted me-3" size={20} />
								<h6 className="mb-0 text-dark">Métricas de Permanência</h6>
							</div>
						</Card.Header>
						<Card.Body className="p-4">
							<Row className="g-3">
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaClock className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{formatTimeFromSeconds(
													permanenceMetrics.tempoMedioPermanencia
												)}
											</h6>
											<small className="text-muted">Tempo Médio</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaHourglassStart
													className="text-muted"
													size={24}
												/>
											</div>
											<h6 className="text-muted mb-1">
												{permanenceMetrics.quantidadeMenor24h.toLocaleString()}
											</h6>
											<small className="text-muted">&lt; 24h</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaHourglassHalf className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{permanenceMetrics.quantidadeEntre24h48h.toLocaleString()}
											</h6>
											<small className="text-muted">24h - 48h</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaHourglassEnd className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{permanenceMetrics.quantidadeEntre48h72h.toLocaleString()}
											</h6>
											<small className="text-muted">48h - 72h</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaHourglass className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{permanenceMetrics.quantidadeMaior72h.toLocaleString()}
											</h6>
											<small className="text-muted">&gt; 72h</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={2} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaUsers className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{permanenceMetrics.totalPermanencias.toLocaleString()}
											</h6>
											<small className="text-muted">
												Total de Permanências
											</small>
										</div>
									</div>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Taxa de Ocupação */}
			<Row>
				<Col>
					<Card className="border-0 shadow-sm rounded">
						<Card.Header className="bg-white border-bottom py-3">
							<div className="d-flex align-items-center">
								<FaBed className="text-muted me-3" size={20} />
								<h6 className="mb-0 text-dark">Taxa de Ocupação</h6>
							</div>
						</Card.Header>
						<Card.Body className="p-4">
							<Row className="g-3">
								<Col sm={12} md={6} lg={3} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaUsers className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{occupationRate.totalOcupacoes.toLocaleString()}
											</h6>
											<small className="text-muted">Total de Ocupações</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={3} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaBed className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{occupationRate.totalLeitos.toLocaleString()}
											</h6>
											<small className="text-muted">Total de Leitos</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={3} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaCalendarAlt className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{occupationRate.diasPeriodo.toLocaleString()}
											</h6>
											<small className="text-muted">Dias do Período</small>
										</div>
									</div>
								</Col>
								<Col sm={12} md={6} lg={3} className="mb-3">
									<div className="card border border-muted bg-white h-100 shadow-sm">
										<div className="card-body text-center p-3">
											<div className="mb-2">
												<FaPercentage className="text-muted" size={24} />
											</div>
											<h6 className="text-muted mb-1">
												{occupationRate.taxaOcupacao.toFixed(2)}%
											</h6>
											<small className="text-muted">Taxa de Ocupação</small>
										</div>
									</div>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</div>
	);
}
